import { PDFTemplate, ResearchPDFData, AppIdeaPDFData, ContentPDFData } from './pdf-types';
import { formatDateForPDF, markdownToHTML } from './pdf-utils';

/**
 * Base CSS styles for all PDF templates
 */
const BASE_STYLES = `
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      background: white;
      padding: 20px;
    }
    
    .header {
      border-bottom: 3px solid #f97316;
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    
    .title {
      font-size: 28px;
      font-weight: bold;
      color: #1f2937;
      margin-bottom: 10px;
    }
    
    .subtitle {
      font-size: 16px;
      color: #6b7280;
      margin-bottom: 5px;
    }
    
    .meta-info {
      font-size: 14px;
      color: #9ca3af;
    }
    
    .section {
      margin-bottom: 30px;
    }
    
    .section-title {
      font-size: 20px;
      font-weight: bold;
      color: #374151;
      margin-bottom: 15px;
      padding-bottom: 5px;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .content {
      font-size: 14px;
      line-height: 1.8;
    }
    
    .content h1, .content h2, .content h3 {
      margin: 20px 0 10px 0;
      color: #1f2937;
    }
    
    .content h1 { font-size: 22px; }
    .content h2 { font-size: 18px; }
    .content h3 { font-size: 16px; }
    
    .content p {
      margin-bottom: 12px;
    }
    
    .content ul, .content ol {
      margin: 10px 0 10px 20px;
    }
    
    .content li {
      margin-bottom: 5px;
    }
    
    .sources {
      background: #f9fafb;
      padding: 15px;
      border-radius: 8px;
      border-left: 4px solid #f97316;
    }
    
    .source-item {
      margin-bottom: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .source-item:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
    }
    
    .source-title {
      font-weight: bold;
      color: #1f2937;
    }
    
    .source-url {
      color: #3b82f6;
      font-size: 12px;
      word-break: break-all;
    }
    
    .related-questions {
      background: #fef3c7;
      padding: 15px;
      border-radius: 8px;
      border-left: 4px solid #f59e0b;
    }
    
    .question-item {
      margin-bottom: 8px;
      padding-left: 15px;
      position: relative;
    }
    
    .question-item:before {
      content: "•";
      color: #f59e0b;
      font-weight: bold;
      position: absolute;
      left: 0;
    }
    
    .features-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 15px;
      margin: 15px 0;
    }
    
    .feature-item {
      background: #f0f9ff;
      padding: 10px;
      border-radius: 6px;
      border-left: 3px solid #0ea5e9;
    }
    
    .footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 12px;
      color: #9ca3af;
    }
    
    @media print {
      body { padding: 0; }
      .header { page-break-after: avoid; }
      .section { page-break-inside: avoid; }
    }
  </style>
`;

/**
 * Research PDF Template
 */
export const researchTemplate: PDFTemplate = {
  id: 'research',
  name: 'Research Report',
  description: 'Professional template for research content with sources and related questions',
  template: (data: ResearchPDFData) => `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${data.title}</title>
      ${BASE_STYLES}
    </head>
    <body>
      <div class="header">
        <div class="title">${data.title}</div>
        <div class="subtitle">Research Type: ${data.researchType}</div>
        <div class="meta-info">Generated on ${formatDateForPDF(data.generatedAt)}</div>
      </div>
      
      <div class="section">
        <div class="section-title">Research Content</div>
        <div class="content">
          ${markdownToHTML(data.content)}
        </div>
      </div>
      
      ${data.sources && data.sources.length > 0 ? `
        <div class="section">
          <div class="section-title">Sources</div>
          <div class="sources">
            ${data.sources.map(source => `
              <div class="source-item">
                <div class="source-title">${source.title}</div>
                <div class="source-url">${source.url}</div>
                ${source.description ? `<div style="margin-top: 5px; font-size: 13px; color: #6b7280;">${source.description}</div>` : ''}
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}
      
      ${data.relatedQuestions && data.relatedQuestions.length > 0 ? `
        <div class="section">
          <div class="section-title">Related Questions</div>
          <div class="related-questions">
            ${data.relatedQuestions.map(question => `
              <div class="question-item">${question}</div>
            `).join('')}
          </div>
        </div>
      ` : ''}
      
      ${data.parameters && Object.keys(data.parameters).length > 0 ? `
        <div class="section">
          <div class="section-title">Research Parameters</div>
          <div class="content">
            ${Object.entries(data.parameters).map(([key, value]) => `
              <p><strong>${key}:</strong> ${value}</p>
            `).join('')}
          </div>
        </div>
      ` : ''}
      
      <div class="footer">
        Generated by DealDriver Research Assistant
      </div>
    </body>
    </html>
  `,
  defaultOptions: {
    filename: 'research-report',
    format: 'a4',
    orientation: 'portrait',
  }
};

/**
 * App Idea PDF Template
 */
export const appIdeaTemplate: PDFTemplate = {
  id: 'app-idea',
  name: 'App Idea Report',
  description: 'Comprehensive template for app idea documentation',
  template: (data: AppIdeaPDFData) => `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${data.title}</title>
      ${BASE_STYLES}
    </head>
    <body>
      <div class="header">
        <div class="title">${data.title}</div>
        <div class="subtitle">Category: ${data.category}</div>
        <div class="meta-info">Generated on ${formatDateForPDF(data.generatedAt)}</div>
      </div>
      
      <div class="section">
        <div class="section-title">Description</div>
        <div class="content">
          ${markdownToHTML(data.description)}
        </div>
      </div>
      
      ${data.targetAudience ? `
        <div class="section">
          <div class="section-title">Target Audience</div>
          <div class="content">
            ${markdownToHTML(data.targetAudience)}
          </div>
        </div>
      ` : ''}
      
      ${data.features && data.features.length > 0 ? `
        <div class="section">
          <div class="section-title">Key Features</div>
          <div class="features-grid">
            ${data.features.map(feature => `
              <div class="feature-item">${feature}</div>
            `).join('')}
          </div>
        </div>
      ` : ''}
      
      ${data.businessModel ? `
        <div class="section">
          <div class="section-title">Business Model</div>
          <div class="content">
            ${markdownToHTML(data.businessModel)}
          </div>
        </div>
      ` : ''}
      
      ${data.marketAnalysis ? `
        <div class="section">
          <div class="section-title">Market Analysis</div>
          <div class="content">
            ${markdownToHTML(data.marketAnalysis)}
          </div>
        </div>
      ` : ''}
      
      ${data.technicalRequirements && data.technicalRequirements.length > 0 ? `
        <div class="section">
          <div class="section-title">Technical Requirements</div>
          <div class="content">
            <ul>
              ${data.technicalRequirements.map(req => `<li>${req}</li>`).join('')}
            </ul>
          </div>
        </div>
      ` : ''}
      
      ${data.relatedResearch && data.relatedResearch.length > 0 ? `
        <div class="section">
          <div class="section-title">Related Research</div>
          <div class="content">
            ${data.relatedResearch.map(research => `
              <p><strong>${research.title}</strong> (ID: ${research.id})</p>
            `).join('')}
          </div>
        </div>
      ` : ''}
      
      <div class="footer">
        Generated by DealDriver App Ideas Assistant
      </div>
    </body>
    </html>
  `,
  defaultOptions: {
    filename: 'app-idea-report',
    format: 'a4',
    orientation: 'portrait',
  }
};

/**
 * General Content PDF Template
 */
export const contentTemplate: PDFTemplate = {
  id: 'content',
  name: 'Content Document',
  description: 'General purpose template for any content type',
  template: (data: ContentPDFData) => `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${data.title}</title>
      ${BASE_STYLES}
    </head>
    <body>
      <div class="header">
        <div class="title">${data.title}</div>
        <div class="subtitle">Content Type: ${data.contentType}</div>
        ${data.author ? `<div class="subtitle">Author: ${data.author}</div>` : ''}
        <div class="meta-info">Generated on ${formatDateForPDF(data.generatedAt)}</div>
      </div>
      
      <div class="section">
        <div class="content">
          ${markdownToHTML(data.content)}
        </div>
      </div>
      
      ${data.metadata && Object.keys(data.metadata).length > 0 ? `
        <div class="section">
          <div class="section-title">Metadata</div>
          <div class="content">
            ${Object.entries(data.metadata).map(([key, value]) => `
              <p><strong>${key}:</strong> ${value}</p>
            `).join('')}
          </div>
        </div>
      ` : ''}
      
      <div class="footer">
        Generated by DealDriver Content Assistant
      </div>
    </body>
    </html>
  `,
  defaultOptions: {
    filename: 'content-document',
    format: 'a4',
    orientation: 'portrait',
  }
};

/**
 * Available PDF templates
 */
export const PDF_TEMPLATES = {
  research: researchTemplate,
  appIdea: appIdeaTemplate,
  content: contentTemplate,
} as const;

/**
 * Get template by ID
 */
export const getTemplate = (templateId: keyof typeof PDF_TEMPLATES): PDFTemplate => {
  return PDF_TEMPLATES[templateId];
}; 